import React from 'react';
import Axios from '../request';
import FormConsumer, {
  isDriveLicenseQuestion,
  buildFormPayloadFromQuestions,
} from './FormConsumers';
import {
  Flex, Box, Text, ActionNote,
} from '../components';
import { Button } from '../styles';
import { isActionAssigneeMatchCompanyStaffV2 } from '../../../utils';
import { toFormData } from '../../../assets/js/utils';

class ReviewForm extends React.Component {
  constructor(props) {
    super(props);
    const { action } = props;
    let questions = [];
    if (props.previewMode) {
      questions = props.fillFormAction.assignees[0].form_submission.questions;
    } else {
      const employeeFormSubmission = this.getEmployeeFormSubmission(action);
      questions = employeeFormSubmission.questions || [];
    }

    const questionsWithAnswers = questions.map((question) => {
      const { question_type: questionType } = question;
      if (question.answer) {
        if (questionType === 'attachment') {
          const answer = {
            ...question.answer,
            answer: {
              ...question.answer.answer,
              [question.id]: question.answer.answer.file_name,
            },
          };
          return {
            ...question,
            answer,
          };
        }
        return question;
      }
      const answer = {
        answer: {
          [question.id]: (() => {
            let value = '';
            if (questionType === 'multiple_selection') {
              value = [];
            }
            return value;
          })(),
        },
      };
      if (question.question_type === 'dl') {
        answer.answer.state_code = '';
      }
      return {
        ...question,
        answer,
      };
    });

    this.formConsumer = React.createRef();

    this.state = {
      questions: questionsWithAnswers,
      editQuestionIds: [],
      redoQuestionIds: [],
      redoNotes: {},
      actionStatus: action.status,

      // Do not change the value of `readOnlyQuestions`, this is only for query the original answer when reviewer uncheck
      // the question edit button
      readOnlyQuestions: [...questionsWithAnswers],
      saveDataError: '',
    };
  }

  getEmployeeFormSubmission(action) {
    const employeeFormSubmission = _.find(
      action.form_submissions,
      (s) => s.employee_onboarding_action.action_type === 'employee_fill_form',
    ) || {};

    return employeeFormSubmission;
  }

  handleQuestionChange({ question, value }) {
    const questions = this.state.questions.map((q) => {
      if (q.id !== question.id) return q;
      let answer;
      if (isDriveLicenseQuestion(question) || question.question_type === 'attachment') {
        answer = {
          ...q.answer,
          answer: {
            ...q.answer.answer,
            ...value,
          },
        };
      } else {
        answer = {
          ...q.answer,
          answer: {
            ...q.answer.answer,
            [q.id]: value,
          },
        };
      }
      const updatedQuestion = {
        ...q,
        answer,
      };
      return updatedQuestion;
    });
    this.setState({ questions });
  }

  handleEditCheckChange({ question, checked }) {
    const { id: questionId } = question;
    let editQuestionIds = [...this.state.editQuestionIds];
    let redoQuestionIds = [...this.state.redoQuestionIds];
    let questions = [...this.state.questions];
    if (checked) {
      editQuestionIds.push(questionId);
      redoQuestionIds = redoQuestionIds.filter((id) => id !== questionId);
    } else {
      editQuestionIds = editQuestionIds.filter((id) => id !== questionId);
      questions = questions.map((q) => {
        if (q.id !== questionId) return q;
        return _.find(this.state.readOnlyQuestions, {
          id: questionId,
        });
      });
      // restore original answer
    }
    this.setState({ editQuestionIds, redoQuestionIds, questions });
  }

  handleRedoCheckChange({ question, checked }) {
    const { id: questionId } = question;
    let redoQuestionIds = [...this.state.redoQuestionIds];
    let editQuestionIds = [...this.state.editQuestionIds];
    let questions = [...this.state.questions];
    if (checked) {
      redoQuestionIds.push(questionId);
      editQuestionIds = editQuestionIds.filter((id) => id !== questionId);
      // restore orignal answer if needed
      questions = questions.map((q) => {
        if (q.id !== questionId) return q;
        return _.find(this.state.readOnlyQuestions, {
          id: questionId,
        });
      });
    } else {
      redoQuestionIds = redoQuestionIds.filter((id) => id !== questionId);
    }
    this.setState({ redoQuestionIds, editQuestionIds, questions });
  }

  handleRedoNoteChange({ question, note }) {
    const redoNotes = {
      ...this.state.redoNotes,
      [question.id]: note,
    };
    this.setState({ redoNotes });
  }

  hanldeClickSubmitButton() {
    // validate the form
    this.formConsumer.current.validate((error) => {
      if (error) return;
      // NO error, proceed to submit
      // 1. prepare edit payload
      // 2. config HTTP request
      // 3. submit to server
      // 4. handle server response

      const { action } = this.props;
      let payload = {};

      if (this.state.editQuestionIds.length > 0) {
        const editedQuestions = this.state.questions.filter((q) => this.state.editQuestionIds.includes(q.id));
        const formPayload = editedQuestions.reduce(
          (acc, question) => {
            const { id: questionId } = question;
            const { answer } = question.answer;
            acc[questionId] = answer;
            return acc;
          },
          {},
        );

        payload = {
          onboarding_form_submissions: [
            {
              id: this.getEmployeeFormSubmission(action).id,
              answers: formPayload,
            },
          ],
        };
      }

      const { action: { id: actionId } } = this.props;
      const config = {
        method: 'POST',
        url: APIHOST + '/v1/employee_onboarding_actions/:id/approve'.replace(':id', actionId),
        data: toFormData(payload),
      };
      this.setState({ isSubmitting: true });
      Axios(config)
        .then((responseData) => {
          this.setState({ isSubmitting: false });
          if (this.props.onApproveSuccess) {
            this.props.onApproveSuccess(responseData);
          }
        })
        .catch((error) => {
          let saveDataError = 'Something went wrong while saving data.';
          if (error && error.response && error.response.data) {
            const { error_code: errorCode, message } = error.response.data;
            if (message) {
              saveDataError = message;
            }
            if (errorCode === 43001 || errorCode === 43002) {
              // Need to refresh the side bar action status
              if (this.props.refreshProgressDataFn) this.props.refreshProgressDataFn();
            }
          }
          this.setState({ isSubmitting: false, saveDataError });
        });
    });
  }

  handleClickResubmitButton() {
    const { questions } = this.state;
    const { action } = this.props;
    const { action: { id: actionId } } = this.props;
    const { editQuestionIds } = this.state;
    const digestKey = this.getEmployeeFormSubmission(action).digest_key;

    const payload = {
      onboarding_form_submission: {
        bad_answers: { ...this.state.redoNotes },
      },
    };

    if (editQuestionIds.length > 0) {
      const formPayload = buildFormPayloadFromQuestions(questions);
      payload.onboarding_form_submission.answers = formPayload;
    }

    const config = {
      method: 'POST',
      url: APIHOST
        + '/v1/onboarding_form_submissions/:digest_key/request_resubmit'.replace(
          ':digest_key',
          digestKey,
        ),
      data: toFormData(payload),
      headers: {
        ...(authCode && { Authorization: authCode }),
      },
    };
    this.setState({ isSubmitting: true });
    Axios(config)
      .then((responseData) => {
        this.setState({ isSubmitting: false });
        if (this.props.onApproveSuccess) {
          this.props.onApproveSuccess(responseData);
        }
      })
      .catch((error) => {
        let saveDataError = 'Something went wrong while saving data.';
        if (error && error.response && error.response.data) {
          const { error_code: errorCode, message } = error.response.data;
          if (message) {
            saveDataError = message;
          }
          if (errorCode === 43001 || errorCode === 43002) {
            // Need to refresh the side bar action status
            if (this.props.refreshProgressDataFn) this.props.refreshProgressDataFn();
          }
        }
        this.setState({ isSubmitting: false, saveDataError });
      });
  }

  renderApprovalButtons() {
    let disabled = false;
    let disableNote = '';

    if (this.props.viewResultAsManager) {
      return null;
    }

    if (this.props.previewMode) {
      disabled = true;
      disableNote = 'Cannot submit form in preview mode.';
    } else {
      let assigneeName = '';
      const { action } = this.props;
      const { action: { assignees } } = this.props;
      assigneeName = assignees
        && assignees[0]
        && assignees[0].company_staff
        && assignees[0].company_staff.name;
      if (action.status === 'completed') {
        disabled = true;
        disableNote = 'This step has been completed.';
      }
      if (this.getEmployeeFormSubmission(action).status === 'not_started') {
        disabled = true;
        disableNote = 'Employee did not submit form yet.';
      } else if (!isActionAssigneeMatchCompanyStaffV2(assignees, companyStaff)) {
        disabled = true;
        disableNote = `This action should be done by ${assigneeName}`;
      }
    }

    let buttonTitle = 'Approve';
    const { editQuestionIds, saveDataError } = this.state;
    const hasEditQuestion = editQuestionIds.length > 0;
    if (hasEditQuestion) {
      buttonTitle = 'Save changes and approve';
    }

    return (
      <Flex column alignItems="flex-end">
        <Box>
          <Button
            disabled={disabled}
            loading={this.state.isSubmitting}
            onClick={this.hanldeClickSubmitButton.bind(this)}
          >
            {buttonTitle}
          </Button>
        </Box>
        {disabled && <ActionNote text={disableNote} />}
        {saveDataError && (
        <Flex justifyContent="flex-end" mt={1}>
          <Text color="red">{saveDataError}</Text>
        </Flex>
        )}
      </Flex>
    );
  }

  renderAskResubmitButtons() {
    let disabled = false;
    let disableNote = '';

    if (this.props.viewResultAsManager) {
      return null;
    }

    if (this.props.previewMode) {
      disabled = true;
      disableNote = 'Cannot submit in preview mode.';
    } else {
      const { action } = this.props;
      const { action: { assignees } } = this.props;
      if (!isActionAssigneeMatchCompanyStaffV2(action.assignees, companyStaff)) {
        const assigneeName = assignees
          && assignees[0]
          && assignees[0].company_staff
          && assignees[0].company_staff.name;
        disabled = true;
        disableNote = `This action should be done by ${assigneeName}`;
      }
    }

    const { editQuestionIds, saveDataError } = this.state;
    const hasEditQuestion = editQuestionIds.length > 0;
    let buttonTitle = 'Ask to re-submit';
    if (hasEditQuestion) {
      buttonTitle = 'Save changes and ask to re-submit';
    }

    return (
      <Flex column alignItems="flex-end">
        <Box>
          <Button
            disabled={disabled}
            loading={this.state.isSubmitting}
            onClick={this.handleClickResubmitButton.bind(this)}
          >
            {buttonTitle}
          </Button>
        </Box>

        {disabled && <ActionNote text={disableNote} />}
        {saveDataError && (
        <Flex justifyContent="flex-end" mt={1}>
          <Text color="red">{saveDataError}</Text>
        </Flex>
        )}
      </Flex>
    );
  }

  render() {
    const { questions, editQuestionIds, redoQuestionIds } = this.state;
    return (
      <Flex column>
        <Flex column style={{ width: '368px', margin: 'auto' }}>
          <Flex mb={20}>
            <Text fontSize="22px" bold>{this.props.title}</Text>
          </Flex>
          <FormConsumer
            questions={questions}
            onQuestionChange={this.handleQuestionChange.bind(this)}
            ref={this.formConsumer}
            // Property related to review action
            reviewActions={this.props.viewResultAsManager ? [] : ['edit', 'redo']}
            editQuestionIds={editQuestionIds}
            redoQuestionIds={redoQuestionIds}
            redoNotes={this.state.redoNotes}
            onEditCheckChange={this.handleEditCheckChange.bind(this)}
            onRedoCheckChange={this.handleRedoCheckChange.bind(this)}
            onRedoNoteChange={this.handleRedoNoteChange.bind(this)}
          />

          {/* render buttons */}
          <Flex mt={24} column>
            {!redoQuestionIds.length > 0 && this.renderApprovalButtons()}
            {redoQuestionIds.length > 0 && this.renderAskResubmitButtons()}
          </Flex>
        </Flex>

      </Flex>
    );
  }
}

export default ReviewForm;
